import { css } from "@emotion/css";
import { Snackbar } from "@octopusdeploy/design-system-components";
import { useEffect, useState, useRef } from "react";
import { findColorInScales } from "./filterUtils";
type ColorTokenDetailProps = {
    cssCustomPropertyLabel: string;
    colorPath?: string;
};
export function ColorTokenDetail({ cssCustomPropertyLabel, colorPath }: ColorTokenDetailProps) {
    const [isHovered, setIsHovered] = useState(false);
    const [colorValue, setColorValue] = useState("");
    const [colorScalePath, setColorScalePath] = useState<string | null>(null);
    const [copied, setCopied] = useState<string>("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarValueToDisplay, setSnackbarValueToDisplay] = useState("");
    const observerRef = useRef<MutationObserver | null>(null);
    const themeChangeListenerRef = useRef<number | null>(null);
    // Reset copied state after 2 seconds
    useEffect(() => {
        if (copied) {
            const timeout = setTimeout(() => {
                setCopied("");
            }, 2000);
            return () => clearTimeout(timeout);
        }
    }, [copied]);
    const copyToClipboard = (text: string, type: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(type);
            setSnackbarValueToDisplay(text);
            setSnackbarOpen(true);
        });
    };
    useEffect(() => {
        // Function to get the computed color value
        const updateColorValue = () => {
            const documentElement = document.documentElement;
            const computedStyle = getComputedStyle(documentElement);
            const propertyName = cssCustomPropertyLabel.replace(/^var\(/, "").replace(/\)$/, "");
            const computedValue = computedStyle.getPropertyValue(propertyName).trim();
            setColorValue(computedValue);
            // Find the color in colorScales
            if (cssCustomPropertyLabel.startsWith("#")) {
                setColorScalePath(findColorInScales(cssCustomPropertyLabel));
            }
            else if (computedValue.startsWith("#")) {
                setColorScalePath(findColorInScales(computedValue));
            }
        };
        // Initial update
        updateColorValue();
        // Set up mutation observer to watch for changes to the head element
        // This will detect when the theme style element is added or updated
        if (!observerRef.current) {
            observerRef.current = new MutationObserver(() => {
                // Use requestAnimationFrame to ensure styles have been applied
                if (themeChangeListenerRef.current) {
                    cancelAnimationFrame(themeChangeListenerRef.current);
                }
                themeChangeListenerRef.current = requestAnimationFrame(() => {
                    updateColorValue();
                    themeChangeListenerRef.current = null;
                });
            });
            observerRef.current.observe(document.head, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }
        return () => {
            // Clean up observers and event listeners
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            if (themeChangeListenerRef.current) {
                cancelAnimationFrame(themeChangeListenerRef.current);
                themeChangeListenerRef.current = null;
            }
        };
    }, [cssCustomPropertyLabel]);
    return (<div className={styles.box}>
            <div className={styles.thumbnail} style={{ backgroundColor: cssCustomPropertyLabel }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}></div>
            {isHovered && <div className={styles.preview} style={{ backgroundColor: cssCustomPropertyLabel }}></div>}
            <div className={styles.colorValue} onClick={() => copyToClipboard(cssCustomPropertyLabel, "token")}>
                {cssCustomPropertyLabel}
            </div>

            <div className={styles.colorValue} onClick={() => copyToClipboard(colorValue, "color")}>
                {colorValue}
            </div>

            {colorPath && (<div className={styles.colorValue} onClick={() => copyToClipboard(colorPath, "path")}>
                    {colorPath}
                </div>)}

            {colorScalePath && (<div className={styles.colorValue} onClick={() => copyToClipboard(colorScalePath, "scale")}>
                    {colorScalePath}
                </div>)}

            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} textAlign="center" content={<span>
                        <pre>{snackbarValueToDisplay}</pre> copied to clipboard
                    </span>}/>
        </div>);
}
const styles = {
    thumbnail: css({
        height: "2.5em",
        width: "2.5em",
        position: "relative",
        left: "-0.7em",
        top: "0.15em",
    }),
    box: css({
        display: "flex",
        flexWrap: "wrap",
        gap: "1em",
        position: "relative",
        fontFamily: "monospace",
    }),
    preview: css({
        width: "100px",
        height: "100px",
        position: "absolute",
        top: "-103px",
        left: "67px",
    }),
    colorValue: css({
        marginLeft: "8px",
        cursor: "pointer",
        transition: "opacity 0.2s",
        "&:hover": {
            opacity: 0.8,
        },
    }),
};
